import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Activity, MailCheck, MailOpen, MousePointerClick, AlertTriangle,
  RefreshCw, ExternalLink, Mail, Loader2, LogOut, Shield,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_KEY_LS = 'nofee_admin_key';

const STATE_META = {
  clicked:    { label: 'Clicked',     pill: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40', icon: MousePointerClick },
  opened:     { label: 'Opened',      pill: 'bg-sky-500/15 text-sky-300 border-sky-500/40',             icon: MailOpen },
  sent:       { label: 'Sent',        pill: 'bg-white/[0.06] text-white/70 border-white/15',            icon: MailCheck },
  churn_risk: { label: 'Churn risk',  pill: 'bg-rose-500/15 text-rose-300 border-rose-500/45',          icon: AlertTriangle },
};

const FILTERS = [
  { key: 'all',        label: 'All' },
  { key: 'churn_risk', label: 'Churn risk' },
  { key: 'opened',     label: 'Opened only' },
  { key: 'clicked',    label: 'Clicked' },
];

const fmtRelative = (iso) => {
  if (!iso) return '—';
  try {
    const diffMs = Date.now() - new Date(iso).getTime();
    const h = diffMs / 3_600_000;
    if (h < 1) return `${Math.round(h * 60)}m ago`;
    if (h < 24) return `${Math.round(h)}h ago`;
    return `${Math.round(h / 24)}d ago`;
  } catch { return '—'; }
};

const GoLiveEngagementAdmin = () => {
  const [adminKey, setAdminKey] = useState(() => {
    try { return localStorage.getItem(ADMIN_KEY_LS) || ''; } catch { return ''; }
  });
  const [authed, setAuthed] = useState(!!adminKey);
  const [keyInput, setKeyInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({ rows: [], summary: {} });
  const [filter, setFilter] = useState('all');

  const fetchData = async (key) => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API}/admin/go-live-engagement`, {
        headers: { 'X-Admin-Key': key },
      });
      setData(r.data);
      setAuthed(true);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 403) {
        setError('Invalid admin key.');
        setAuthed(false);
        try { localStorage.removeItem(ADMIN_KEY_LS); } catch {}
      } else {
        setError(err?.response?.data?.detail || 'Could not load engagement data.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (adminKey) fetchData(adminKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSubmitKey = (e) => {
    e.preventDefault();
    if (!keyInput.trim()) return;
    try { localStorage.setItem(ADMIN_KEY_LS, keyInput.trim()); } catch {}
    setAdminKey(keyInput.trim());
    fetchData(keyInput.trim());
  };

  const onSignOut = () => {
    try { localStorage.removeItem(ADMIN_KEY_LS); } catch {}
    setAdminKey('');
    setKeyInput('');
    setAuthed(false);
    setData({ rows: [], summary: {} });
  };

  const rows = useMemo(() => {
    const all = data.rows || [];
    if (filter === 'all') return all;
    return all.filter((r) => r.state === filter);
  }, [data, filter]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4" data-testid="engagement-admin-gate">
        <form
          onSubmit={onSubmitKey}
          className="w-full max-w-md rounded-2xl border border-yellow-500/30 bg-zinc-950 p-7"
        >
          <div className="w-10 h-10 mb-4 rounded-xl bg-yellow-500/[0.10] border border-yellow-500/40 flex items-center justify-center">
            <Shield className="w-5 h-5 text-yellow-300" />
          </div>
          <h1 className="text-xl font-extrabold tracking-tight mb-1">Admin sign-in</h1>
          <p className="text-[13px] text-white/55 mb-5">
            Enter the NoFee admin key to view Go-Live email engagement.
          </p>
          <input
            type="password"
            value={keyInput}
            onChange={(e) => setKeyInput(e.target.value)}
            placeholder="X-Admin-Key"
            data-testid="engagement-admin-key-input"
            className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/12 focus:border-yellow-500/60 outline-none text-[14px] mb-3"
            autoFocus
          />
          {error && <p className="mb-3 text-[12.5px] text-rose-300">{error}</p>}
          <button
            type="submit"
            data-testid="engagement-admin-key-submit"
            className="w-full px-4 py-3 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-bold text-[14px]"
          >
            Sign in
          </button>
        </form>
      </div>
    );
  }

  const s = data.summary || {};

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white antialiased" data-testid="engagement-admin-page">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-20 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-yellow-500/[0.10] border border-yellow-500/40 flex items-center justify-center">
              <Activity className="w-4 h-4 text-yellow-300" />
            </div>
            <div>
              <p className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">
                Admin
              </p>
              <h1 className="text-[15px] font-bold leading-none mt-0.5">Go-Live engagement</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fetchData(adminKey)}
              disabled={loading}
              data-testid="engagement-refresh-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/12 hover:border-white/25 text-white/80 hover:text-white text-[12.5px] font-semibold transition-all"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Refresh
            </button>
            <button
              type="button"
              onClick={onSignOut}
              data-testid="engagement-signout-btn"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/12 hover:border-rose-500/40 text-white/55 hover:text-rose-300 text-[12.5px] font-semibold transition-all"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-7 pb-20">
        {/* Summary tiles */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="engagement-summary">
          {[
            { key: 'sent',       label: 'Sent (waiting)', value: s.sent || 0,       Icon: MailCheck,         tone: 'border-white/12 bg-white/[0.02]' },
            { key: 'opened',     label: 'Opened',         value: s.opened || 0,     Icon: MailOpen,          tone: 'border-sky-500/30 bg-sky-500/[0.05]' },
            { key: 'clicked',    label: 'Clicked',        value: s.clicked || 0,    Icon: MousePointerClick, tone: 'border-emerald-500/30 bg-emerald-500/[0.05]' },
            { key: 'churn_risk', label: 'Churn risk',     value: s.churn_risk || 0, Icon: AlertTriangle,     tone: 'border-rose-500/30 bg-rose-500/[0.05]' },
          ].map(({ key, label, value, Icon, tone }) => (
            <div key={key} data-testid={`engagement-tile-${key}`} className={`p-4 rounded-2xl border ${tone}`}>
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 text-white/55" />
                <span className="text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-bold">{label}</span>
              </div>
              <div className="mt-2 text-3xl font-extrabold tabular-nums">{value}</div>
            </div>
          ))}
        </div>

        {/* Filter chips */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="engagement-filters">
          {FILTERS.map((f) => {
            const active = f.key === filter;
            return (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilter(f.key)}
                data-testid={`engagement-filter-${f.key}`}
                className={`px-3.5 py-1.5 rounded-full border text-[12px] font-bold transition-all ${
                  active
                    ? 'bg-yellow-500/[0.10] border-yellow-500/45 text-yellow-200'
                    : 'border-white/10 hover:border-white/25 text-white/55 hover:text-white/80'
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {/* Table */}
        <div className="mt-5 rounded-2xl border border-white/[0.06] bg-white/[0.015] overflow-hidden">
          {error && (
            <div className="p-4 text-rose-300 text-[13.5px]">{error}</div>
          )}
          {rows.length === 0 && !loading ? (
            <div className="p-10 text-center text-white/45 text-[14px]">
              No restaurants match this filter yet.
            </div>
          ) : (
            <ul className="divide-y divide-white/[0.05]">
              {rows.map((r) => {
                const meta = STATE_META[r.state] || STATE_META.sent;
                const Icon = meta.icon;
                return (
                  <li key={r.restaurant_id} data-testid={`engagement-row-${r.restaurant_id}`} className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[15px] font-bold text-white truncate">{r.restaurant_name || '—'}</span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10.5px] uppercase tracking-wider font-bold ${meta.pill}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                        {r.followup_sent_at && (
                          <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-white/[0.05] border border-white/10 text-white/55">
                            Follow-up sent
                          </span>
                        )}
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11.5px] text-white/45">
                        <span>Sent <span className="text-white/75">{fmtRelative(r.sent_at)}</span></span>
                        <span>Opened <span className="text-white/75">{fmtRelative(r.opened_at)}</span></span>
                        <span>Clicked <span className="text-white/75">{fmtRelative(r.clicked_at)}</span></span>
                        <span className="tabular-nums">{r.hours_since_sent}h since sent</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {r.slug && (
                        <a
                          href={`/menu/${r.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`engagement-view-menu-${r.restaurant_id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/12 hover:border-yellow-500/40 text-white/80 hover:text-white text-[12px] font-bold transition-all"
                        >
                          Menu <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {r.state === 'churn_risk' && !r.followup_sent_at && (
                        <span
                          data-testid={`engagement-auto-followup-${r.restaurant_id}`}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-rose-500/30 bg-rose-500/[0.06] text-rose-200 text-[11.5px] font-semibold"
                        >
                          <Mail className="w-3.5 h-3.5" /> Auto follow-up queued
                        </span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <p className="mt-5 text-[11.5px] text-white/40 leading-relaxed max-w-2xl">
          <strong className="text-white/65">How it works:</strong> Restaurants are flagged as "Churn risk" when they
          received the Go-Live email more than 48 hours ago and have not yet clicked the dashboard CTA. A consultative
          follow-up email is queued automatically every hour for these restaurants — once per restaurant.
        </p>
      </div>
    </div>
  );
};

export default GoLiveEngagementAdmin;
